import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface DataSource {
  id: string
  name: string
  type: string
  connection_type: string
  status: 'active' | 'degraded' | 'inactive'
  description: string
  metadata: Record<string, any>
  datasets_produced: string[]
  created_at: string
  last_sync: string
}

interface Dataset {
  id: string
  name: string
  description: string
  source_id?: string
  schema: {
    fields: string[]
    row_count: number
    size_mb: number
  }
  quality_score: number
  freshness: string
  upstream?: string[]
  downstream?: string[]
  created_at: string
  last_updated: string
}

interface Transformation {
  id: string
  name: string
  type: string
  description: string
  input_datasets: string[]
  output_datasets: string[]
  logic: string
  schedule: string
  last_run: string
  avg_duration_seconds: number
  status: string
}

interface LineageGraph {
  nodes: Array<{
    id: string
    label: string
    type: string
    status?: string
    quality_score?: number
    transform_type?: string
  }>
  edges: Array<{
    source: string
    target: string
    type: string
  }>
  statistics: {
    total_sources: number
    total_datasets: number
    total_transformations: number
    total_edges: number
  }
}

interface Statistics {
  data_sources: { total: number; active: number; degraded: number }
  datasets: { total: number; derived: number; source_based: number }
  transformations: { total: number; successful: number; failed: number }
  average_quality_score: number
}

const API_BASE = '/api'

export default function DataLineagePage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'sources' | 'datasets' | 'transformations' | 'graph'>('overview')
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [sources, setSources] = useState<DataSource[]>([])
  const [datasets, setDatasets] = useState<Dataset[]>([])
  const [transformations, setTransformations] = useState<Transformation[]>([])
  const [lineageGraph, setLineageGraph] = useState<LineageGraph | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_selectedDataset, setSelectedDataset] = useState<Dataset | null>(null)
  const [impactAnalysis, setImpactAnalysis] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showImpactModal, setShowImpactModal] = useState(false)

  useEffect(() => {
    fetchAllData()
  }, [])

  const fetchAllData = async () => {
    try {
      setLoading(true)
      const [statsRes, sourcesRes, datasetsRes, transformRes, graphRes] = await Promise.all([
        fetch(`${API_BASE}/lineage/statistics`),
        fetch(`${API_BASE}/lineage/sources`),
        fetch(`${API_BASE}/lineage/datasets`),
        fetch(`${API_BASE}/lineage/transformations`),
        fetch(`${API_BASE}/lineage/graph`)
      ])

      const stats = await statsRes.json()
      const sourcesData = await sourcesRes.json()
      const datasetsData = await datasetsRes.json()
      const transformData = await transformRes.json()
      const graphData = await graphRes.json()

      setStatistics(stats)
      setSources(sourcesData.sources || [])
      setDatasets(datasetsData.datasets || [])
      setTransformations(transformData.transformations || [])
      setLineageGraph(graphData)
    } catch (error) {
      console.error('Error fetching lineage data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchImpactAnalysis = async (datasetId: string) => {
    try {
      const response = await fetch(`${API_BASE}/lineage/datasets/${datasetId}/impact`)
      const data = await response.json()
      setImpactAnalysis(data)
      setShowImpactModal(true)
    } catch (error) {
      console.error('Error fetching impact analysis:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'success': return 'bg-green-500'
      case 'degraded': case 'warning': return 'bg-orange-500'
      case 'inactive': case 'failed': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getQualityColor = (score: number) => {
    if (score >= 90) return 'text-green-600'
    if (score >= 80) return 'text-yellow-600'
    return 'text-red-600'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="card p-6">
          <div className="text-sm text-gray-500">Data Sources</div>
          <div className="text-2xl font-bold">{statistics?.data_sources.total}</div>
          <div className="text-sm text-gray-400">
            <span className="text-green-600">{statistics?.data_sources.active} active</span>
            {statistics?.data_sources.degraded ? ` / ${statistics?.data_sources.degraded} degraded` : ''}
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Datasets</div>
          <div className="text-2xl font-bold">{statistics?.datasets.total}</div>
          <div className="text-sm text-gray-400">
            {statistics?.datasets.derived} derived / {statistics?.datasets.source_based} source-based
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Transformations</div>
          <div className="text-2xl font-bold">{statistics?.transformations.total}</div>
          <div className="text-sm text-gray-400">
            <span className="text-green-600">{statistics?.transformations.successful} successful</span>
            {statistics?.transformations.failed ? ` / ${statistics?.transformations.failed} failed` : ''}
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500">Avg Quality Score</div>
          <div className={`text-2xl font-bold ${getQualityColor(statistics?.average_quality_score || 0)}`}>
            {statistics?.average_quality_score?.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Data Flow Summary */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Data Flow Summary</h3>
        <div className="flex items-center justify-between gap-4">
          <div className="text-center flex-1">
            <div className="bg-blue-100 rounded-lg p-4">
              <div className="text-3xl font-bold text-blue-600">{lineageGraph?.statistics.total_sources}</div>
              <div className="text-sm text-blue-800">Sources</div>
            </div>
          </div>
          <div className="text-gray-400 text-2xl">→</div>
          <div className="text-center flex-1">
            <div className="bg-purple-100 rounded-lg p-4">
              <div className="text-3xl font-bold text-purple-600">{lineageGraph?.statistics.total_transformations}</div>
              <div className="text-sm text-purple-800">Transformations</div>
            </div>
          </div>
          <div className="text-gray-400 text-2xl">→</div>
          <div className="text-center flex-1">
            <div className="bg-green-100 rounded-lg p-4">
              <div className="text-3xl font-bold text-green-600">{lineageGraph?.statistics.total_datasets}</div>
              <div className="text-sm text-green-800">Datasets</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Active Data Sources</h3>
          <div className="space-y-3">
            {sources.slice(0, 4).map((source) => (
              <div key={source.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(source.status)}`}></div>
                  <div>
                    <div className="font-medium">{source.name}</div>
                    <div className="text-xs text-gray-500">{source.type} | {source.connection_type}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  Last sync: {formatDate(source.last_sync)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Transformations</h3>
          <div className="space-y-3">
            {transformations.slice(0, 4).map((transform) => (
              <div key={transform.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(transform.status)}`}></div>
                  <div>
                    <div className="font-medium">{transform.name}</div>
                    <div className="text-xs text-gray-500">{transform.type} | {transform.schedule}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {transform.avg_duration_seconds}s avg
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const renderSources = () => (
    <div className="space-y-4">
      {sources.map((source) => (
        <div key={source.id} className="card p-6">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              <div className={`w-4 h-4 rounded-full mt-1 ${getStatusColor(source.status)}`}></div>
              <div>
                <h3 className="text-lg font-semibold">{source.name}</h3>
                <p className="text-gray-600 text-sm">{source.description}</p>
                <div className="flex gap-4 mt-2 text-sm text-gray-500">
                  <span>Type: {source.type}</span>
                  <span>Connection: {source.connection_type}</span>
                  <span>Status: {source.status}</span>
                </div>
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="text-gray-500">Last Sync</div>
              <div className="font-medium">{formatDate(source.last_sync)}</div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <div className="text-sm font-medium mb-2">Metadata</div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              {Object.entries(source.metadata).map(([key, value]) => (
                <div key={key} className="bg-gray-50 p-2 rounded">
                  <div className="text-gray-500">{key.replace(/_/g, ' ')}</div>
                  <div className="font-medium">{String(value)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-sm font-medium mb-2">Produces Datasets</div>
            <div className="flex gap-2">
              {source.datasets_produced.map((ds) => (
                <span key={ds} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                  {ds}
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  const renderDatasets = () => (
    <div className="space-y-4">
      {datasets.map((dataset) => (
        <div key={dataset.id} className="card p-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold">{dataset.name}</h3>
              <p className="text-gray-600 text-sm">{dataset.description}</p>
              <div className="flex gap-4 mt-2 text-sm">
                <span className={getQualityColor(dataset.quality_score)}>
                  Quality: {dataset.quality_score}%
                </span>
                <span className="text-gray-500">Freshness: {dataset.freshness}</span>
                <span className="text-gray-500">Rows: {dataset.schema.row_count.toLocaleString()}</span>
                <span className="text-gray-500">Size: {dataset.schema.size_mb} MB</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedDataset(dataset)
                  fetchImpactAnalysis(dataset.id)
                }}
                className="btn btn-sm btn-outline"
              >
                Impact Analysis
              </button>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-medium mb-2">Schema Fields</div>
                <div className="flex flex-wrap gap-1">
                  {dataset.schema.fields.map((field) => (
                    <span key={field} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                      {field}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Dependencies</div>
                <div className="space-y-1">
                  {dataset.upstream && dataset.upstream.length > 0 && (
                    <div className="text-sm">
                      <span className="text-gray-500">Upstream: </span>
                      {dataset.upstream.join(', ')}
                    </div>
                  )}
                  {dataset.downstream && dataset.downstream.length > 0 && (
                    <div className="text-sm">
                      <span className="text-gray-500">Downstream: </span>
                      {dataset.downstream.join(', ')}
                    </div>
                  )}
                  {!dataset.upstream?.length && !dataset.downstream?.length && (
                    <div className="text-sm text-gray-400">No dependencies</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  const renderTransformations = () => (
    <div className="space-y-4">
      {transformations.map((transform) => (
        <div key={transform.id} className="card p-6">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-4">
              <div className={`w-4 h-4 rounded-full mt-1 ${getStatusColor(transform.status)}`}></div>
              <div>
                <h3 className="text-lg font-semibold">{transform.name}</h3>
                <p className="text-gray-600 text-sm">{transform.description}</p>
                <div className="flex gap-4 mt-2 text-sm text-gray-500">
                  <span>Type: {transform.type}</span>
                  <span>Schedule: {transform.schedule}</span>
                  <span>Avg Duration: {transform.avg_duration_seconds}s</span>
                </div>
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="text-gray-500">Last Run</div>
              <div className="font-medium">{formatDate(transform.last_run)}</div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t">
            <div className="text-sm font-medium mb-2">Logic</div>
            <code className="bg-gray-100 p-2 rounded block text-sm">{transform.logic}</code>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium mb-2">Input Datasets</div>
              <div className="flex flex-wrap gap-2">
                {transform.input_datasets.map((ds) => (
                  <span key={ds} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                    {ds}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-2">Output Datasets</div>
              <div className="flex flex-wrap gap-2">
                {transform.output_datasets.map((ds) => (
                  <span key={ds} className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                    {ds}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  const renderGraph = () => (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">Lineage Graph</h3>
        <p className="text-gray-600 mb-4">Visual representation of data flow through the system</p>

        {/* Simple graph visualization */}
        <div className="bg-gray-50 rounded-lg p-6 min-h-[400px]">
          <div className="flex flex-col gap-8">
            {/* Sources Row */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-3">Data Sources</div>
              <div className="flex gap-4 flex-wrap">
                {lineageGraph?.nodes.filter(n => n.type === 'source').map((node) => (
                  <div key={node.id} className="bg-blue-100 border-2 border-blue-300 rounded-lg p-3 min-w-[150px]">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${getStatusColor(node.status || 'active')}`}></div>
                      <span className="font-medium text-sm">{node.label}</span>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">{node.type}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Arrows Down */}
            <div className="text-center text-gray-400 text-2xl">↓</div>

            {/* Transformations Row */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-3">Transformations</div>
              <div className="flex gap-4 flex-wrap">
                {lineageGraph?.nodes.filter(n => n.type === 'transformation').map((node) => (
                  <div key={node.id} className="bg-purple-100 border-2 border-purple-300 rounded-lg p-3 min-w-[150px]">
                    <span className="font-medium text-sm">{node.label}</span>
                    <div className="text-xs text-purple-600 mt-1">{node.transform_type}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Arrows Down */}
            <div className="text-center text-gray-400 text-2xl">↓</div>

            {/* Datasets Row */}
            <div>
              <div className="text-sm font-medium text-gray-500 mb-3">Datasets</div>
              <div className="flex gap-4 flex-wrap">
                {lineageGraph?.nodes.filter(n => n.type === 'dataset').map((node) => (
                  <div key={node.id} className="bg-green-100 border-2 border-green-300 rounded-lg p-3 min-w-[150px]">
                    <span className="font-medium text-sm">{node.label}</span>
                    {node.quality_score !== undefined && (
                      <div className={`text-xs mt-1 ${getQualityColor(node.quality_score)}`}>
                        Quality: {node.quality_score}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Total connections: {lineageGraph?.statistics.total_edges}
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Data Lineage & Provenance</h1>
            <p className="text-gray-600">Track data origins, transformations, and dependencies</p>
          </div>
          <button onClick={fetchAllData} className="btn btn-outline" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b">
          {['overview', 'sources', 'datasets', 'transformations', 'graph'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`pb-2 px-1 capitalize ${activeTab === tab ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="card p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading lineage data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'sources' && renderSources()}
            {activeTab === 'datasets' && renderDatasets()}
            {activeTab === 'transformations' && renderTransformations()}
            {activeTab === 'graph' && renderGraph()}
          </>
        )}
      </main>

      {/* Impact Analysis Modal */}
      {showImpactModal && impactAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Impact Analysis</h3>
                <button onClick={() => setShowImpactModal(false)} className="text-gray-500 hover:text-gray-700">
                  X
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="font-medium">{impactAnalysis.source_dataset?.name}</div>
                  <div className="text-sm text-gray-500">Changes to this dataset will affect:</div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {impactAnalysis.impact_summary?.total_impacted_datasets}
                    </div>
                    <div className="text-sm text-gray-600">Total Impacted</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">
                      {impactAnalysis.impact_summary?.direct_dependencies}
                    </div>
                    <div className="text-sm text-gray-600">Direct Dependencies</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {impactAnalysis.impact_summary?.impacted_transformations}
                    </div>
                    <div className="text-sm text-gray-600">Transformations</div>
                  </div>
                </div>

                {impactAnalysis.impacted_datasets?.length > 0 && (
                  <div>
                    <div className="font-medium mb-2">Impacted Datasets</div>
                    <div className="space-y-2">
                      {impactAnalysis.impacted_datasets.map((ds: any) => (
                        <div key={ds.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span>{ds.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${ds.impact_level === 'direct' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'}`}>
                            {ds.impact_level}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {impactAnalysis.impacted_transformations?.length > 0 && (
                  <div>
                    <div className="font-medium mb-2">Impacted Transformations</div>
                    <div className="space-y-2">
                      {impactAnalysis.impacted_transformations.map((tr: any) => (
                        <div key={tr.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span>{tr.name}</span>
                          <span className="text-xs text-gray-500">{tr.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .btn {
          padding: 0.5rem 1rem;
          border-radius: 0.375rem;
          font-weight: 500;
          transition: all 0.15s ease;
        }
        .btn-outline {
          border: 1px solid #d1d5db;
          background: white;
        }
        .btn-outline:hover {
          background: #f3f4f6;
        }
        .btn-outline:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.875rem;
        }
        .card {
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  )
}
